import { MainLayout } from "@/components/layout/MainLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { useToast } from "@/hooks/use-toast"
import { ArrowLeft, Save } from "lucide-react"
import { Case } from "@/types"
import { useAuth } from "@/hooks/useAuth"

const NewCase = () => {
	const navigate = useNavigate()
	const { toast } = useToast()
	const { user } = useAuth()

	const [formData, setFormData] = useState({
		case_name: "",
		plaintiff_first_name: "",
		plaintiff_last_name: "",
		plaintiff_date_of_birth: "",
		date_of_incident: "",
		incident_description: "",
	})

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [selectedFiles, setSelectedFiles] = useState<File[]>([])
	const [suggestedName, setSuggestedName] = useState("")

	useEffect(() => {
		const { plaintiff_first_name, plaintiff_last_name, date_of_incident } =
			formData

		const currentSuggestedName =
			`${plaintiff_first_name} ${plaintiff_last_name}${
				date_of_incident ? ` (${date_of_incident})` : ""
			}`.trim()

		if (currentSuggestedName && currentSuggestedName !== suggestedName) {
			setSuggestedName(currentSuggestedName)
		} else if (
			!plaintiff_first_name &&
			!plaintiff_last_name &&
			!date_of_incident
		) {
			setSuggestedName("")
		}
	}, [
		formData.plaintiff_first_name,
		formData.plaintiff_last_name,
		formData.date_of_incident,
	])

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target
		setFormData((prev) => ({ ...prev, [name]: value }))
	}

	const handleFilesSelected = (files: File[]) => {
		setSelectedFiles(files)
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setIsSubmitting(true)

		if (!user) {
			toast({
				title: "Authentication Error",
				description: "You must be logged in to create a case.",
				variant: "destructive",
			})
			setIsSubmitting(false)
			return
		}

		try {
			if (
				!formData.case_name ||
				!formData.plaintiff_first_name ||
				!formData.plaintiff_last_name ||
				!formData.plaintiff_date_of_birth ||
				!formData.date_of_incident
			) {
				toast({
					title: "Missing Fields",
					description: "Please fill out all required fields.",
					variant: "destructive",
				})
				setIsSubmitting(false)
				return
			}

			const token = await user.getIdToken()
			if (!token) {
				toast({
					title: "Authentication Error",
					description:
						"Could not retrieve authentication token. Please try again.",
					variant: "destructive",
				})
				setIsSubmitting(false)
				return
			}

			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/cases/`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify(formData),
				}
			)

			if (!response.ok) {
				const errorData = await response.json()
				throw new Error(
					errorData.detail || `HTTP error! status: ${response.status}`
				)
			}

			const createdCase: Case = await response.json()

			toast({
				title: "Case created successfully",
				description: `Created case "${createdCase.case_name}". You can now upload documents.`,
			})

			console.log("Case created with ID:", createdCase.id)
			console.log("Selected files to upload:", selectedFiles)
			if (selectedFiles.length > 0) {
				toast({
					title: "Next Step",
					description: `Upload documents for case ${createdCase.id} on the next screen.`,
				})
			}

			navigate(`/cases/${createdCase.id}`)
		} catch (error) {
			let message = "Failed to create case"
			if (error instanceof Error) {
				message = error.message
			}
			toast({
				title: "Error",
				description: message,
				variant: "destructive",
			})
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<MainLayout>
			<div className="mb-6">
				<button
					onClick={() => navigate("/cases")}
					className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
				>
					<ArrowLeft className="h-4 w-4 mr-1" />
					Back to Cases
				</button>
				<h1 className="text-2xl font-semibold text-foreground">
					Create New Case
				</h1>
			</div>

			<form onSubmit={handleSubmit} className="space-y-8 max-w-3xl">
				<div className="space-y-6 bg-white p-6 rounded-xl shadow-subtle">
					<div className="grid gap-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="plaintiff_first_name">
									Plaintiff First Name
								</Label>
								<Input
									id="plaintiff_first_name"
									name="plaintiff_first_name"
									placeholder="Enter plaintiff's first name"
									value={formData.plaintiff_first_name}
									onChange={handleChange}
									className="focus-ring"
									required
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="plaintiff_last_name">Plaintiff Last Name</Label>
								<Input
									id="plaintiff_last_name"
									name="plaintiff_last_name"
									placeholder="Enter plaintiff's last name"
									value={formData.plaintiff_last_name}
									onChange={handleChange}
									className="focus-ring"
									required
								/>
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="plaintiff_date_of_birth">
									Plaintiff Date of Birth
								</Label>
								<Input
									id="plaintiff_date_of_birth"
									name="plaintiff_date_of_birth"
									type="date"
									value={formData.plaintiff_date_of_birth}
									onChange={handleChange}
									className="focus-ring"
									required
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="date_of_incident">Date of Incident</Label>
								<Input
									id="date_of_incident"
									name="date_of_incident"
									type="date"
									value={formData.date_of_incident}
									onChange={handleChange}
									className="focus-ring"
									required
								/>
							</div>
						</div>

						<div className="space-y-2">
							<Label htmlFor="case_name">Case Name</Label>
							{suggestedName && formData.case_name !== suggestedName && (
								<div className="flex items-center justify-between mb-1">
									<p className="text-xs text-muted-foreground italic">
										Suggestion: {suggestedName}
									</p>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="px-2 py-0.5 h-auto text-xs text-primary hover:bg-primary/10"
										onClick={() =>
											setFormData((prev) => ({
												...prev,
												case_name: suggestedName,
											}))
										}
										title={`Use suggested name: ${suggestedName}`}
									>
										Use Suggestion
									</Button>
								</div>
							)}
							<div className="flex items-center space-x-2">
								<Input
									id="case_name"
									name="case_name"
									placeholder="Enter case name or use suggestion"
									value={formData.case_name}
									onChange={handleChange}
									className="focus-ring flex-grow"
									required
								/>
							</div>
						</div>

						<div className="space-y-2">
							<Label htmlFor="incident_description">Incident Description</Label>
							<Textarea
								id="incident_description"
								name="incident_description"
								placeholder="Provide a brief description of the incident (you can edit this later)"
								value={formData.incident_description}
								onChange={handleChange}
								className="focus-ring min-h-[100px]"
							/>
						</div>
					</div>
				</div>

				<div className="flex justify-end space-x-4">
					<Button
						type="button"
						variant="outline"
						onClick={() => navigate("/cases")}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
					<Button type="submit" disabled={isSubmitting}>
						{isSubmitting ? (
							"Creating Case..."
						) : (
							<>
								<Save className="h-4 w-4 mr-2" />
								Create Case
							</>
						)}
					</Button>
				</div>
			</form>
		</MainLayout>
	)
}

export default NewCase
